import React, { useState, useEffect, useRef } from 'react';
import { Button } from "@/components/ui/button";
import { Delete } from "lucide-react";
import { cn } from "@/lib/utils";

interface MobileKeyboardProps {
  onKeyPress: (key: string) => void;
}

const MobileKeyboard: React.FC<MobileKeyboardProps> = ({ onKeyPress }) => {
  const [isNumberKeyboard, setIsNumberKeyboard] = useState(false);
  const [lastPressedKey, setLastPressedKey] = useState<string | null>(null);
  const keyboardRef = useRef<HTMLDivElement>(null);

  const topRow = ['Q', 'W', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P'];
  const middleRow = ['A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L'];
  const bottomRow = ['Z', 'X', 'C', 'V', 'B', 'N', 'M'];

  const numberTopRow = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'];
  const numberMiddleRow = ['@', '#', '$', '%', '&', '*', '-', '+', '(', ')'];
  const numberBottomRow = ['!', '?', ',', '.', ';', ':', '/'];

  useEffect(() => {
    const keyboard = keyboardRef.current;
    if (!keyboard) return;

    const handleTouchStart = (e: TouchEvent) => {
        e.preventDefault();
    };

    const handleTouchMove = (e: TouchEvent) => {
        e.preventDefault();
      e.stopPropagation();
    };

    const handleTouchEnd = (e: TouchEvent) => {
        e.preventDefault();
      e.stopPropagation();
    };

    keyboard.addEventListener('touchstart', handleTouchStart, { passive: false });
    keyboard.addEventListener('touchmove', handleTouchMove, { passive: false });
    keyboard.addEventListener('touchend', handleTouchEnd, { passive: false });
    return () => {
      keyboard.removeEventListener('touchstart', handleTouchStart);
      keyboard.removeEventListener('touchmove', handleTouchMove);
      keyboard.removeEventListener('touchend', handleTouchEnd);
    };
  }, []);

  const handleKeyPress = (key: string) => {
    setLastPressedKey(key);
    onKeyPress(key);
    setTimeout(() => {
      setLastPressedKey(null);
    }, 75);
  };

  const renderKeyboard = () => {
    if (isNumberKeyboard) {
      return (
        <>
          {/* Number Top row */}
          <div className="flex justify-center gap-1 mb-1.5">
            {numberTopRow.map((key) => (
              <Button
                key={key}
                data-key={key}
                variant="outline"
                className={cn(
                  "w-[8.5%] h-12 rounded-xl font-semibold text-lg relative overflow-hidden transition-all duration-75",
                  "bg-white text-black border border-gray-200 shadow-md",
                  "hover:bg-white hover:text-black",
                  lastPressedKey === key ? 
                    "transform translate-y-[-8px] scale-110 bg-gray-100 shadow-lg" : 
                    "transform translate-y-0"
                )}
                onTouchStart={() => handleKeyPress(key)}
              >
                {lastPressedKey === key && (
                  <span className="absolute inset-0 bg-white opacity-20 animate-ping rounded-xl"></span>
                )}
                {key}
              </Button>
            ))}
          </div>
          
          {/* Number Middle row */}
          <div className="flex justify-center gap-1 mb-1.5 px-4">
            {numberMiddleRow.map((key) => (
              <Button
                key={key}
                data-key={key}
                variant="outline"
                className={cn(
                  "w-[9.2%] h-12 rounded-xl font-semibold text-lg relative overflow-hidden transition-all duration-75",
                  "bg-white text-black border border-gray-200 shadow-md",
                  "hover:bg-white hover:text-black",
                  lastPressedKey === key ? 
                    "transform translate-y-[-8px] scale-110 bg-gray-100 shadow-lg" : 
                    "transform translate-y-0"
                )}
                onTouchStart={() => handleKeyPress(key)}
              >
                {lastPressedKey === key && (
                  <span className="absolute inset-0 bg-white opacity-20 animate-ping rounded-xl"></span>
                )}
                {key}
              </Button>
            ))}
          </div>
          
          {/* Number Bottom row */}
          <div className="flex justify-center gap-1.5">
            <Button
              variant="outline"
              data-key="ABC"
              className={cn(
                "w-[12%] h-12 rounded-xl text-sm transition-all duration-75",
                "bg-gray-400 text-black border border-gray-200 shadow-md",
                "hover:bg-gray-400 hover:text-black",
                isNumberKeyboard ? 
                  "bg-gray-400 text-black shadow-inner" : 
                  "bg-gray-400"
              )}
              onTouchStart={() => {
                setIsNumberKeyboard(false);
                // Add a ripple effect when switching keyboard
                const ripple = document.createElement('div');
                ripple.className = 'absolute inset-0 bg-primary rounded-md opacity-10 scale-0 animate-scale-in';
                document.querySelector('.keyboard')?.appendChild(ripple);
                setTimeout(() => ripple.remove(), 500);
              }}
            >
              <span className="relative z-10">ABC</span>
            </Button>
            
            {numberBottomRow.map((key) => (
              <Button
                key={key}
                data-key={key}
                variant="outline"
                className={cn(
                  "w-[8.5%] h-12 rounded-xl font-semibold text-lg relative overflow-hidden transition-all duration-75",
                  "bg-white text-black border border-gray-200 shadow-md",
                  "hover:bg-white hover:text-black",
                  lastPressedKey === key ? 
                    "transform translate-y-[-8px] scale-110 bg-gray-100 shadow-lg" : 
                    "transform translate-y-0"
                )}
                onTouchStart={() => handleKeyPress(key)}
              >
                {lastPressedKey === key && (
                  <span className="absolute inset-0 bg-white opacity-20 animate-ping rounded-xl"></span>
                )}
                {key}
              </Button>
            ))}
            
            <Button
              variant="outline"
              data-key="backspace"
              className={cn(
                "w-[12%] h-12 rounded-xl flex items-center justify-center text-lg [&_svg]:!size-6 relative overflow-hidden transition-all duration-75",
                "bg-gray-400 text-black border border-gray-200 shadow-md",
                "hover:bg-gray-400 hover:text-black",
                lastPressedKey === 'backspace' ? 
                  "transform translate-y-[-8px] scale-110 bg-gray-200 shadow-lg" : 
                  "transform translate-y-0"
              )}
              onClick={() => handleKeyPress('backspace')}
            >
              {lastPressedKey === 'backspace' && (
                <span className="absolute inset-0 bg-white opacity-20 animate-ping rounded-xl"></span>
              )}
              <Delete />
            </Button>
          </div>
        </>
      );
    }

    return (
      <>
        {/* Letter Top row */}
        <div className="flex justify-center gap-1 mb-1.5">
          {topRow.map((key) => (
            <Button
              key={key}
              data-key={key}
              variant="outline"
              className={cn(
                "w-[8.5%] h-12 rounded-xl font-semibold text-lg relative overflow-hidden transition-all duration-75",
                "bg-white text-black border border-gray-200 shadow-md",
                "hover:bg-white hover:text-black",
                lastPressedKey === key ? 
                  "transform translate-y-[-8px] scale-110 bg-gray-100 shadow-lg" : 
                  "transform translate-y-0"
              )}
             onTouchStart={() => handleKeyPress(key)}
            >
              {lastPressedKey === key && (
                <span className="absolute inset-0 bg-white opacity-20 animate-ping rounded-xl"></span>
              )}
              {key}
            </Button>
          ))}
        </div>
        
        {/* Letter Middle row */}
        <div className="flex justify-center gap-1.5 mb-1.5 px-4">
          {middleRow.map((key) => (
            <Button
              key={key}
              data-key={key}
              variant="outline"
              className={cn(
                "w-[9.2%] h-12 rounded-xl font-semibold text-lg relative overflow-hidden transition-all duration-75",
                "bg-white text-black border border-gray-200 shadow-md",
                "hover:bg-white hover:text-black",
                lastPressedKey === key ? 
                  "transform translate-y-[-8px] scale-110 bg-gray-100 shadow-lg" : 
                  "transform translate-y-0"
              )}
              onTouchStart={() => handleKeyPress(key)}
            >
              {lastPressedKey === key && (
                <span className="absolute inset-0 bg-white opacity-20 animate-ping rounded-xl"></span>
              )}
              {key}
            </Button>
          ))}
        </div>
        
        {/* Letter Bottom row */}
        <div className="flex justify-center gap-1.5">
          <Button
            variant="outline"
            data-key="123"
            className={cn(
              "w-[12%] h-12 rounded-xl text-sm transition-all duration-75",
              "bg-gray-400 text-black border border-gray-200 shadow-md",
              "hover:bg-gray-400 hover:text-black",
              !isNumberKeyboard ? 
                "bg-gray-400 text-black shadow-inner" : 
                "bg-gray-400"
            )}
            onTouchStart={() => {
              setIsNumberKeyboard(true);
              // Add a ripple effect when switching keyboard
              const ripple = document.createElement('div');
              ripple.className = 'absolute inset-0 bg-primary rounded-md opacity-10 scale-0 animate-scale-in';
              document.querySelector('.keyboard')?.appendChild(ripple);
              setTimeout(() => ripple.remove(), 500);
            }}
          >
            <span className="relative z-10">123</span>
          </Button>
          
          {bottomRow.map((key) => (
            <Button
              key={key}
              data-key={key}
              variant="outline"
              className={cn(
                "w-[8.5%] h-12 rounded-xl font-semibold text-lg relative overflow-hidden transition-all duration-75",
                "bg-white text-black border border-gray-200 shadow-md",
                "hover:bg-white hover:text-black",
                lastPressedKey === key ? 
                  "transform translate-y-[-8px] scale-110 bg-gray-100 shadow-lg" : 
                  "transform translate-y-0"
              )}
              onTouchStart={() => handleKeyPress(key)}
            >
              {lastPressedKey === key && (
                <span className="absolute inset-0 bg-white opacity-20 animate-ping rounded-xl"></span>
              )}
              {key}
            </Button>
          ))}
          
          <Button
            variant="outline"
            data-key="backspace"
            className={cn(
              "w-[12%] h-12 rounded-xl flex items-center justify-center text-lg [&_svg]:!size-6 relative overflow-hidden transition-all duration-75",
              "bg-gray-400 text-black border border-gray-200 shadow-md",
              "hover:bg-gray-400 hover:text-black",
              lastPressedKey === 'backspace' ? 
                "transform translate-y-[-8px] scale-110 bg-gray-200 shadow-lg" : 
                "transform translate-y-0"
            )}
            onTouchStart={() => handleKeyPress('backspace')}
          >
            {lastPressedKey === 'backspace' && (
              <span className="absolute inset-0 bg-white opacity-20 animate-ping rounded-xl"></span>
            )}
            <Delete />
          </Button>
        </div>
      </>
    );
  };

  return (
    <div 
      ref={keyboardRef}
      className="keyboard w-full p-2 pb-6 bg-gray-200 relative"
    >
      <div className="keyboard-wrapper relative">
        {renderKeyboard()}
      </div>
    </div>
  );
};

export default MobileKeyboard;
